import { ChevronsDown, ChevronsUp } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '#router'
import type { BrowseProblem } from '@/components/problems/types'

interface TopicWithCount {
  name: string
  slug: string
  count: number
}

function deriveTopicsWithCounts(problems: BrowseProblem[]): TopicWithCount[] {
  const counts = new Map<string, number>()
  const namesBySlug = new Map<string, string>()
  for (const p of problems) {
    for (const topic of p.topics) {
      counts.set(topic.slug, (counts.get(topic.slug) ?? 0) + 1)
      namesBySlug.set(topic.slug, topic.name)
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([slug, count]) => ({
      name: namesBySlug.get(slug) ?? slug,
      slug,
      count,
    }))
}

export function TopicPills({ problems }: { problems: BrowseProblem[] }) {
  const topics = useMemo(() => deriveTopicsWithCounts(problems), [problems])
  const [expanded, setExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [singleRowHeight, setSingleRowHeight] = useState<number | null>(null)

  // Measure row height and detect overflow in a single pass.
  // biome-ignore lint/correctness/useExhaustiveDependencies: topics gates the early return below, so the ref only attaches once topics populate — re-run measurement then
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const check = () => {
      const firstChild = el.firstElementChild as HTMLElement | null
      if (!firstChild) return
      const style = getComputedStyle(firstChild)
      const verticalMargin =
        parseFloat(style.marginTop) + parseFloat(style.marginBottom)
      const rowH = firstChild.offsetHeight + verticalMargin
      setSingleRowHeight(rowH)
      setHasOverflow(el.scrollHeight > rowH + 2)
    }

    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [topics])

  if (topics.length === 0) return null

  return (
    <div className="border-border mb-4 overflow-hidden border-b pb-4">
      <div className="relative flex">
        <div
          ref={containerRef}
          className="relative my-[-4px] ml-[-4px] flex flex-wrap overflow-hidden sm:my-[-10px] sm:ml-[-10px]"
          style={
            !expanded && singleRowHeight
              ? { maxHeight: singleRowHeight }
              : undefined
          }
        >
          {topics.map((topic) => (
            <div
              key={topic.slug}
              className="group m-1 flex items-center sm:m-[10px]"
            >
              <Link
                href={`/problems/topics/${topic.slug}`}
                className="inline-flex items-center"
              >
                <span className="text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 whitespace-nowrap text-sm">
                  {topic.name}
                </span>
                <span className="bg-muted text-muted-foreground group-hover:bg-blue-100 group-hover:text-blue-600 dark:group-hover:bg-blue-900/30 dark:group-hover:text-blue-400 ml-0.5 flex h-[18px] items-center justify-center rounded-[10px] px-1 text-xs tabular-nums transition-colors sm:ml-1 sm:px-1.5">
                  {topic.count}
                </span>
              </Link>
            </div>
          ))}
        </div>
        {(hasOverflow || expanded) && (
          <div className="text-muted-foreground absolute bottom-[-7px] right-0 z-10 flex self-end">
            <span className="from-background dark:from-background h-9 w-6 bg-gradient-to-l" />
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="bg-background hover:text-foreground flex h-9 cursor-pointer items-center pl-0.5 text-sm transition-colors"
            >
              <span className="grow text-right">
                {expanded ? 'Collapse' : 'Expand'}
              </span>
              {expanded ? (
                <ChevronsUp className="h-[15px] w-[15px]" />
              ) : (
                <ChevronsDown className="h-[15px] w-[15px]" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
